// src/components/InvocationCard/InvocationCard.tsx
import React, { useRef, useState } from 'react';
import { downloadCardAsImage } from '../../utils/downloadUtils';
import { Analytics } from '../../utils/analytics';
import './InvocationCard.css';

interface Formula {
  title: string;
  arabic: string;
  phonetic?: string;
}

interface InvocationProps {
  id: number;
  type: 'invocation';
  title?: string;
  arabicText: string;
  phonetic?: string;
  translation: string;
  context?: string;
  afterContext?: string;
  source?: string;
  formulas?: Formula[];
  showDownload?: boolean;
}

const InvocationCard: React.FC<InvocationProps> = ({
  id,
  title,
  arabicText,
  phonetic,
  translation,
  context,
  afterContext,
  source,
  formulas,
  showDownload = true
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    if (!cardRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      await downloadCardAsImage(cardRef.current, {
        fileName: `invocation-${id}`,
        scale: 2,
        watermark: {
          logoPath: '/images/logoP&F.jpg',
          text: 'Traduit par Mâhi Bâyân'
        }
      });
      
      // Track successful download
      Analytics.trackInvocationDownload(id.toString(), title || `Invocation ${id}`);
      
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Une erreur est survenue lors du téléchargement.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Fonction pour formater le texte avec les débuts de hadiths en gras
  const formatText = (text: string) => {
    // Patterns pour identifier les débuts de hadiths et citations
    const patterns = [
      /(Le Prophète ﷺ a dit :)/g,
      /(Allah dit :)/g,
      /(Le Prince des croyants.*? a dit :)/g,
      /(Le Prophète ﷺ a dit en voulant annoncer une bonne nouvelle :)/g,
      /(Ce hadith a été expliqué par un autre hadith où le Prophète ﷺ dit :)/g,
      /(Lorsque le Prophète ﷺ était.*? il disait :)/g,
      /(Le Prophète ﷺ a dit : « Lorsque.*? dit : »)/g,
      /(Le Prophète ﷺ a dit : « Quiconque.*? dit : »)/g,
      /(Le Prophète ﷺ réunissait les gens de son foyer et leur disait :)/g,
      /(Le Prophète ﷺ a dit à Oubey :)/g,
      /(Lorsque les Compagnons.*? entendirent cela, ils dirent :)/g,
      /(Le Prophète ﷺ répondit :)/g,
      /(« Donne-nous du réconfort par la prière, ô Bilal ! »)/g,
      /(Il n'y a pas une personne qui est touchée par les soucis et la tristesse profonde et qui dit)/g
    ];

    let formattedText = text;
    
    // Ajouter les deux points à la fin pour l'invocation 2
    if (text.includes("Il n'y a pas une personne qui est touchée par les soucis et la tristesse profonde et qui dit") && !text.endsWith(" :")) {
      formattedText = formattedText + " :";
    }
    
    // Appliquer les patterns pour mettre en gras
    patterns.forEach(pattern => {
      formattedText = formattedText.replace(pattern, '<strong>$1</strong>');
    });

    // Convertir les \n\n en paragraphes HTML
    formattedText = formattedText.replace(/\n\n/g, '</p><p>');
    formattedText = '<p>' + formattedText + '</p>';

    return formattedText;
  };

  return (
    <div
      ref={cardRef}
      className="invocation"
      style={{ borderLeftColor: '#059669' }}
    >
      <div className="invocation-header">
        <div className={`invocation-number ${id === 999 ? 'verse-indicator' : ''}`}>
          {id === 999 ? '📖' : id}
        </div>
        {title && id !== 2 ? (
          <h3 className="invocation-title">{title}</h3>
        ) : id === 2 && title ? (
          <div 
            className="hadith-context header-context"
            style={{ 
              background: '#fffbeb', 
              borderLeft: '3px solid #f59e0b',
              color: '#92400e'
            }}
            dangerouslySetInnerHTML={{ __html: formatText(title) }}
          />
        ) : (context && id !== 9) ? (
          <div 
            className="hadith-context header-context"
            dangerouslySetInnerHTML={{ __html: formatText(context) }}
          />
        ) : null}
      </div>


      {arabicText && (
        <div className="arabic-text">{arabicText}</div>
      )}

      {phonetic && (
        <div className="phonetic">
          {phonetic}
        </div>
      )}

      {translation && (
        <div 
          className="translation"
          dangerouslySetInnerHTML={{ __html: formatText(translation) }}
        />
      )}

      {formulas && formulas.map((formula, index) => (
        <div key={index}>
          <h4 className="formula-title">{formula.title}</h4>
          <div className="arabic-text">{formula.arabic}</div>
          {formula.phonetic && (
            <div className="phonetic">
              {formula.phonetic}
            </div>
          )}
        </div>
      ))}

      {afterContext && (
        <div 
          className="hadith-context"
          dangerouslySetInnerHTML={{ __html: formatText(afterContext) }}
        />
      )}

      {/* Instructions de répétition - uniquement pour certaines invocations qui en ont besoin */}
      {context && !title?.includes(context) && id !== 2 && ![4, 5, 6, 7, 8].includes(id) && (
        <div 
          className="hadith-context"
          style={{ 
            background: '#fff7ed', 
            borderLeft: '3px solid #f59e0b',
            color: '#92400e',
            fontWeight: '500',
            marginTop: '15px'
          }}
          dangerouslySetInnerHTML={{ __html: formatText(context) }}
        />
      )}

      {source && (
        <div className="source-container">
          <div className="source">{source}</div>
        </div>
      )}

      {showDownload && (
        <button
          className="download-button"
          onClick={handleDownload}
          disabled={isDownloading}
          aria-label="Télécharger cette invocation"
        >
          {isDownloading ? (
            <>
              <span className="loading-spinner">⏳</span>
              Téléchargement...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <polyline 
                  points="7 10 12 15 17 10" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <line 
                  x1="12" 
                  y1="15" 
                  x2="12" 
                  y2="3" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              Télécharger
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default InvocationCard;
